/**
 * Students dashboard section: tabbed list with create/edit slide panel.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { StudentsDataTable } from '@/components/students/inperson/StudentsDataTable';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreateStudentView } from '@/components/students/inperson/CreateStudentView';
import { EditStudentView } from '@/components/students/inperson/EditStudentView';
import { usePageActions } from '@/hooks/usePageActions';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link StudentsSection}. */
interface StudentsSectionProps {
  onSuccess?: () => void;
  layoutWidth?: ElementWidth;
}

/** Dashboard widget: in-person students list. */
export const StudentsSection: React.FC<StudentsSectionProps> = ({
  onSuccess,
  layoutWidth = 'full',
}) => {
  const queryClient = useQueryClient();
  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('students');
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const [editStudentId, setEditStudentId] = React.useState<string | null>(null);

  const closeForm = () => {
    setCreateFormOpen(false);
    setEditStudentId(null);
  };

  const handleSuccess = () => {
    closeForm();
    void queryClient.invalidateQueries({ queryKey: ['users', 'student'] });
    onSuccess?.();
  };

  return (
    <>
      <StudentsDataTable
        layoutWidth={layoutWidth}
        isSection={true}
        onEditStudent={(id) => {
          setEditStudentId(id);
          setCreateFormOpen(false);
        }}
        showCreateButton={canCreate}
        onCreateStudent={() => {
          setCreateFormOpen(true);
          setEditStudentId(null);
        }}
        pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
      />

      <SlidePanel isOpen={createFormOpen} onClose={closeForm}>
        <CreateStudentView onClose={closeForm} onSuccess={handleSuccess} />
      </SlidePanel>

      <SlidePanel isOpen={editStudentId !== null} onClose={closeForm}>
        {editStudentId && (
          <EditStudentView
            key={editStudentId}
            studentId={editStudentId}
            onClose={closeForm}
            onSuccess={handleSuccess}
          />
        )}
      </SlidePanel>
    </>
  );
};
